'use client'
import React, { useMemo, useCallback, useState, useEffect } from 'react'
import dynamic from 'next/dynamic'

import { useTranslations, useLocale } from 'next-intl'
import { usePathname } from '@/src/i18n/navigation'

import { languageOptions, currencyOptions } from '@/src/constants/header'
import {
    getUserMenuItems,
    getCTNavLinks,
    getUserDisplayName,
    getNavIcon
} from '@/src/utils/header/headerUtils'
import { getActiveVertical } from '@/src/utils/navigation/getActiveVertical'
import { useHeaderAuth } from '@/src/hooks/header/useHeaderAuth'
import { useHeaderCurrency } from '@/src/hooks/header/useHeaderCurrency'
import { useHeaderLanguage } from '@/src/hooks/header/useHeaderLanguage'
import { useHeaderVisibility } from '@/src/hooks/header/useHeaderVisibility'
import { useHeaderUI } from '@/src/hooks/header/useHeaderUI'

import { CTHeader } from './CTHeader'

const SignInModalWrapper = dynamic(
    () => import(/* webpackChunkName: "signin-modal" */ '../flyin/SignInModal/SignInModalWrapper'),
    { ssr: false }
)

interface CTHeaderContainerProps {
    hideSignIn?: boolean
}

const CTHeaderContainer: React.FC<CTHeaderContainerProps> = ({ hideSignIn = false }) => {
    const locale = useLocale()
    const pathname = usePathname()
    const t = useTranslations('header')
    const tCurrency = useTranslations('Currency')

    const { user, handleSignOut, isMounted } = useHeaderAuth()
    const currencyHook = useHeaderCurrency(isMounted)
    const languageHook = useHeaderLanguage('cleartrip', isMounted)
    const visibility = useHeaderVisibility('cleartrip')
    const uiHook = useHeaderUI()

    const activeVertical = useMemo(() => {
        if (!isMounted) return null
        return getActiveVertical()
    }, [isMounted, pathname])

    const [selected, setSelected] = useState({
        language: languageHook.selectedLanguage,
        currency: currencyOptions[0] // Always start with default to prevent hydration mismatch
    })

    useEffect(() => {
        setSelected(prev => ({
            ...prev,
            language: languageHook.selectedLanguage
        }))
    }, [languageHook.selectedLanguage])

    useEffect(() => {
        if (!isMounted) return // Don't update until client-side hydration is complete
        if (!currencyHook.isLoading && !currencyHook.isError && currencyHook.selectedCurrency) {
            const found = currencyHook.currencyOptions.find(opt => opt.code === currencyHook.selectedCurrency!.code)
            if (found) {
                setSelected(prev => ({
                    ...prev,
                    currency: found
                }))
            }
        } else if (!currencyHook.isLoading && !currencyHook.isError && !currencyHook.selectedCurrency) {
            // If no selected currency, use default
            setSelected(prev => ({
                ...prev,
                currency: currencyOptions[0]
            }))
        }
    }, [isMounted, currencyHook.selectedCurrency, currencyHook.isLoading, currencyHook.isError, currencyHook.currencyOptions])

    const navLinks = useMemo(() => getCTNavLinks(locale, activeVertical), [locale, activeVertical])
    const userMenuItems = useMemo(() => getUserMenuItems(t, locale), [t, locale])
    const userDisplayName = useMemo(() => getUserDisplayName(user), [user])

    const options = useMemo(
        () => ({
            language: languageOptions,
            currency: currencyHook.currencyOptions
        }),
        [currencyHook.currencyOptions]
    )

    const onSelect = useCallback(
        (type: 'language' | 'currency', value: any) => {
            setSelected(prev => ({ ...prev, [type]: value }))
            if (type === 'currency') {
                currencyHook.onCurrencySelect(value)
            } else if (type === 'language') {
                languageHook.onLanguageSelect(value)
            }
            uiHook.onMenuClose('user')
        },
        [currencyHook, languageHook, uiHook]
    )

    const displayUser = isMounted ? user : { signedIn: false }

    return (
        <>
            <CTHeader
                navLinks={navLinks}
                options={options}
                selected={selected}
                menuAnchors={{ user: uiHook.menuStates.user ? uiHook.menuAnchors.user : null }}
                onMenuOpen={(menu, event) => uiHook.onMenuOpen(menu, event)}
                onMenuClose={(menu) => uiHook.onMenuClose(menu)}
                onSelect={onSelect}
                onModalOpen={uiHook.onModalOpen}
                t={t}
                tCurrency={tCurrency}
                user={displayUser}
                userDisplayName={userDisplayName}
                userMenuItems={userMenuItems}
                onSignOut={handleSignOut}
                showLangCurrDropdown={visibility.showLangCurrDropdown}
                getNavIcon={getNavIcon}
                hideSignIn={visibility.shouldHideSignIn || hideSignIn}
                showDeals={visibility.showDeals}
                locale={locale}
            />
            <SignInModalWrapper
                open={uiHook.modals.signIn}
                onClose={() => uiHook.onModalClose('signIn')}
                onMenuClose={() => uiHook.onMenuClose('user')}
            />
        </>
    )
}

export { CTHeaderContainer }
export default CTHeaderContainer
